'use server';

/**
 * Kontora BFF – Login Server Action
 *
 * Verarbeitet das Login-Formular, ruft die interne API auf und setzt den Session-Cookie.
 */

import { cookies } from 'next/headers';
import { callInternalApi, InternalApiError } from '../api-client/internal-api-client';
import { setCsrfCookie } from '../csrf/csrf.helper';
import { createBoundCsrfToken } from '../csrf/bound-csrf.helper';
import { setSessionCookie } from '../cookies/session-cookie';
import { logger } from '../../lib/logger';

const COOKIE_SECURE = process.env['SESSION_COOKIE_SECURE'] !== 'false';
const LOG_CONTEXT = 'login.action';

export interface LoginActionState {
  success?: boolean;
  redirectTo?: string;
  error?: string;
  errorCode?: string;
}

export async function loginAction(
  _prevState: LoginActionState,
  formData: FormData,
): Promise<LoginActionState> {
  const email = formData.get('email')?.toString()?.trim();
  const password = formData.get('password')?.toString();
  const twoFactorCode = formData.get('twoFactorCode')?.toString().trim();

  if (!email || !password) {
    return { error: 'E-Mail und Passwort sind erforderlich.', errorCode: 'VALIDATION_FAILED' };
  }

  let redirectTo = '/dashboard';
  try {
    const response = await callInternalApi<{
      token: string;
      user: { publicId: string; email: string; displayName: string | null };
      session: {
        expiresAt: string;
        activeTenantPublicId: string | null;
        roles: string[];
        csrfSecret?: string;
        twoFactorSetupRequired?: boolean;
      };
    }>('POST', '/auth/login', {
      body: { email, password, ...(twoFactorCode ? { twoFactorCode } : {}) },
    });

    if (!response.success || !response.data?.token) {
      return { error: 'Login fehlgeschlagen.', errorCode: 'INTERNAL_ERROR' };
    }

    const sessionToken = response.data.token;
    if (response.data.session.twoFactorSetupRequired) redirectTo = '/einstellungen/2fa';

    // CSRF-Token generieren (lokal), an Session binden und serverseitig registrieren.
    const csrfToken = await createBoundCsrfToken(sessionToken, response.data.session.csrfSecret);

    // Session-Cookie setzen (HttpOnly, Secure, SameSite=Lax)
    await setSessionCookie(sessionToken);
    await setCsrfCookie(sessionToken, response.data.session.csrfSecret, undefined, csrfToken);

    const cookieStore = await cookies();
    cookieStore.set(
      'kontora_user_name',
      response.data.user.displayName || response.data.user.email,
      {
        httpOnly: false,
        secure: COOKIE_SECURE,
        sameSite: 'lax',
        path: '/',
      },
    );
  } catch (error) {
    logger.error({ error }, LOG_CONTEXT, 'Login action error');
    if (error instanceof InternalApiError) {
      if (error.code === 'INVALID_CREDENTIALS') {
        return { error: 'E-Mail oder Passwort ungültig.', errorCode: 'INVALID_CREDENTIALS' };
      }
      if (error.code === 'USER_INACTIVE') {
        return { error: 'Ihr Konto ist nicht aktiv.', errorCode: 'USER_INACTIVE' };
      }
      if (error.code === 'TWO_FACTOR_REQUIRED' || error.code === 'TWO_FACTOR_INVALID') {
        return {
          error:
            error.code === 'TWO_FACTOR_REQUIRED'
              ? 'Bitte Authentifizierungscode oder Backup-Code eingeben und erneut anmelden.'
              : 'Der Authentifizierungscode ist ungültig oder wurde bereits verwendet.',
          errorCode: error.code,
        };
      }
      return {
        error: 'Login fehlgeschlagen. Bitte versuchen Sie es erneut.',
        errorCode: error.code,
      };
    }
    return { error: 'Ein Fehler ist aufgetreten.', errorCode: 'INTERNAL_ERROR' };
  }

  return { success: true, redirectTo };
}
